import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import {
  CheckCircle2, XCircle, Gift, Image as ImageIcon, Download, Send, Receipt, Wallet, MoreHorizontal, ChevronRight, AlertTriangle,
} from "lucide-react";
import AppLayout from "../../components/AppLayout";
import StatusBadge from "../../components/StatusBadge";
import DueChip from "../../components/DueChip";
import SendReminderDialog from "../../components/SendReminderDialog";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { useTerms } from "../../lib/BrandContext";
import { withReturnTo, useSmartBack } from "../../lib/backNavigation";
import { Textarea } from "../../components/ui/textarea";
import api, { formatCurrency, formatDate, API_BASE, getAccessToken, formatMethod } from "../../lib/api";
import { toast } from "sonner";

const STATUS_TABS = [
  { id: "needs-approval", label: "To Review", statuses: ["submitted"] },
  { id: "unpaid",         label: "Unpaid",    statuses: ["unpaid", "rejected"] },
  { id: "paid",           label: "Paid",      statuses: ["confirmed", "waived"] },
];

// URL ?status= back-compat: old per-status URLs map to the new grouped tabs
// so existing deep-links from Home / NeedsAttention / legacy redirects don't
// break.
const URL_STATUS_TO_TAB = {
  "needs-approval": "needs-approval",
  "submitted":      "needs-approval",
  "unpaid":         "unpaid",
  "rejected":       "unpaid",
  "paid":           "paid",
  "confirmed":      "paid",
  "waived":         "paid",
  // "all" no longer exists in v2; default to To Review since that's the
  // most actionable tab.
};

const METHODS = ["venmo", "zelle", "cashapp", "cash", "check", "other"];

/* ─────────────────────────────────────────────────────── */
/* "Record payment" dialog — coach manually logs a payment */
/* ─────────────────────────────────────────────────────── */
function RecordPaymentDialog({ open, onOpenChange, record, onDone }) {
  const [method, setMethod] = useState("venmo");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMethod("venmo");
    setNote("");
  }, [open, record?.id]);

  const save = async () => {
    if (!record?.id) return;
    setSaving(true);
    try {
      await api.post(`/payments/${record.id}/manual-mark`, {
        method,
        coach_note: note || "Recorded by coach",
      });
      toast.success("Payment recorded");
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record payment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="record-payment-dialog">
        <DialogHeader>
          <DialogTitle>Mark Paid</DialogTitle>
          <DialogDescription>
            Record this payment on behalf of the parent. Use this when they
            paid you directly via Venmo, Zelle, Cash App, cash, or check.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg bg-ink-100/60 p-3 text-sm">
            <p className="font-semibold text-ink-900">
              {record?.player?.first_name} {record?.player?.last_name} · {record?.charge?.title}
            </p>
            <p className="text-ink-500">
              {record?.team?.name} · {formatCurrency(record?.amount)} · due {formatDate(record?.charge?.due_date)}
            </p>
          </div>
          <div>
            <Label>How they paid</Label>
            <select value={method} onChange={(e) => setMethod(e.target.value)} className="mt-1 h-11 w-full rounded-lg border border-ink-200 bg-white px-3" data-testid="record-method-select">
              {METHODS.map((m) => <option key={m} value={m}>{formatMethod(m)}</option>)}
            </select>
          </div>
          <div>
            <Label>Note (optional)</Label>
            <Textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. handed me cash at practice"
              data-testid="record-note-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={save}
            disabled={saving}
            className="h-11 bg-brand font-semibold text-white hover:bg-brand-hover"
            data-testid="record-payment-submit"
          >
            <CheckCircle2 className="mr-1 h-4 w-4" />
            {saving ? "Saving…" : "Mark Paid"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────── */
/* One row in the list      */
/* ─────────────────────── */
function PaymentRow({ r, onAct, onRecord, onRemind, onReject }) {
  const { terms } = useTerms();
  const location = useLocation();
  // Preserve full URL (path + tab / status / team_id filters) so Back from
  // fee detail returns to the same Payments view the coach was on.
  const paymentsBackFrom = `${location.pathname}${location.search || ""}`;
  // Method shows for submitted (parent picked it) or confirmed (we know what was used)
  const showMethod = !!r.method && ["submitted", "confirmed"].includes(r.status);

  return (
    <li
      className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:gap-4"
      data-testid={`payment-row-${r.id}`}
    >
      {/* Identity column */}
      <div className="min-w-0 md:flex-[2]">
        <p className="font-semibold text-ink-900 truncate" data-testid={`row-player-${r.id}`} title={`${r.player?.first_name || ""} ${r.player?.last_name || ""}`.trim()}>
          {r.player?.first_name} {r.player?.last_name}
          {r.player?.jersey ? <span className="ml-1 text-ink-500">#{r.player.jersey}</span> : null}
        </p>
        <p className="text-xs text-ink-500 line-clamp-2 break-words" title={`${r.team?.name || ""} · ${r.charge?.title || ""}`}>
          {r.team?.name}{" · "}
          {r.charge?.id ? (
            <Link
              to={withReturnTo(`/coach/charges/${r.charge.id}`, { from: paymentsBackFrom, label: "Payments" })}
              className="inline-flex items-center gap-0.5 font-semibold text-brand hover:underline focus:underline focus:outline-none"
              data-testid={`row-fee-link-${r.id}`}
            >
              {r.charge?.title}
              <ChevronRight className="h-3 w-3" aria-hidden="true" />
            </Link>
          ) : (
            r.charge?.title
          )}
        </p>
        {r.note ? <p className="mt-1 text-xs text-ink-500 line-clamp-2 break-words">Parent note: {r.note}</p> : null}
        {r.coach_note && r.status === "waived" ? (
          <p className="mt-1 text-xs text-ink-500 line-clamp-2 break-words">{terms.leader}: {r.coach_note}</p>
        ) : null}
      </div>

      {/* Amount + due column */}
      <div className="md:w-40 md:text-right">
        <p className="font-heading text-lg font-bold text-ink-900" data-testid={`row-amount-${r.id}`}>
          {formatCurrency(r.amount)}
        </p>
        <p className="text-xs text-ink-500">due {formatDate(r.charge?.due_date)}</p>
      </div>

      {/* Method + status + timing column */}
      <div className="flex flex-wrap items-center gap-2 md:w-56 md:justify-end">
        {showMethod ? (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-ink-100 px-2.5 py-1 text-xs font-semibold text-ink-700"
            data-testid={`row-method-${r.id}`}
          >
            <Receipt className="h-3 w-3" />
            {formatMethod(r.method)}
          </span>
        ) : null}
        <StatusBadge status={r.status} />
        <DueChip dueDate={r.charge?.due_date} paymentStatus={r.status} />
      </div>

      {/* Action column */}
      <div className="flex flex-wrap items-center gap-2 md:w-72 md:justify-end">
        {r.status === "submitted" ? (
          <>
            {r.receipt_path ? (
              <a
                href={`${API_BASE}/files/${r.receipt_path}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-9 items-center gap-1 rounded-md border border-ink-300 px-3 text-sm font-semibold text-ink-700 hover:bg-ink-100"
                data-testid={`row-view-receipt-${r.id}`}
              >
                <ImageIcon className="h-4 w-4" /> Receipt
              </a>
            ) : null}
            <Button
              size="sm"
              className="h-9 bg-emerald-600 font-semibold text-white hover:bg-emerald-700"
              onClick={() => onAct(r.id, "confirm")}
              data-testid={`row-confirm-${r.id}`}
            >
              <CheckCircle2 className="mr-1 h-4 w-4" /> Confirm
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-9 border-rose-300 font-semibold text-rose-700 hover:bg-rose-50"
              onClick={() => onReject(r)}
              data-testid={`row-reject-${r.id}`}
            >
              <XCircle className="mr-1 h-4 w-4" /> Reject
            </Button>
          </>
        ) : null}

        {["unpaid", "rejected"].includes(r.status) ? (
          <>
            <Button
              size="sm"
              className="h-9 bg-brand font-semibold text-white hover:bg-brand-hover"
              onClick={() => onRecord(r)}
              data-testid={`row-record-${r.id}`}
            >
              <Wallet className="mr-1 h-4 w-4" /> Mark Paid
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-9 border-amber-300 font-semibold text-amber-800 hover:bg-amber-50"
              onClick={() => onRemind(r)}
              data-testid={`row-remind-${r.id}`}
            >
              <Send className="mr-1 h-4 w-4" /> Remind
            </Button>
            {/* Less-common action (Waive) lives behind a More menu so the row
                doesn't feel like a transaction ledger. */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-9 w-9 p-0 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
                  data-testid={`row-more-${r.id}`}
                  title="More options"
                  aria-label="More options"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-44 p-1">
                <button
                  type="button"
                  onClick={() => onAct(r.id, "waive", { coach_note: "Waived" })}
                  className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-ink-900 hover:bg-ink-100"
                  data-testid={`row-waive-${r.id}`}
                >
                  <Gift className="h-4 w-4" /> Waive this fee
                </button>
              </PopoverContent>
            </Popover>
          </>
        ) : null}
      </div>
    </li>
  );
}

/* ─────────────────────────────────────────── */
/* Reject dialog — optional reason note         */
/* ─────────────────────────────────────────── */
function RejectDialog({ open, onOpenChange, record, onDone }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setReason(""); }, [open, record?.id]);

  const submit = async () => {
    if (!record?.id) return;
    setSaving(true);
    try {
      await api.post(`/payments/${record.id}/reject`, {
        coach_note: reason || "Rejected by coach",
      });
      toast.success("Payment rejected");
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reject");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reject-payment-dialog">
        <DialogHeader>
          <DialogTitle>Reject payment</DialogTitle>
          <DialogDescription>
            The parent will see this is rejected. Add a short reason so they know
            what to fix.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg bg-ink-100/60 p-3 text-sm">
            <p className="font-semibold text-ink-900">
              {record?.player?.first_name} {record?.player?.last_name} · {record?.charge?.title}
            </p>
            <p className="text-ink-500">
              {formatCurrency(record?.amount)} via {formatMethod(record?.method) || "—"}
            </p>
          </div>
          <div>
            <Label>Reason (optional)</Label>
            <Textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Example: I couldn't verify this payment. Please upload a screenshot or contact me."
              data-testid="reject-reason-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={submit}
            disabled={saving}
            className="h-11 bg-rose-600 font-semibold text-white hover:bg-rose-700"
            data-testid="reject-confirm-button"
          >
            <XCircle className="mr-1 h-4 w-4" />
            {saving ? "Rejecting…" : "Reject payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ──────────────────────────────────────────────────────── */
/* Submission card — rich "approval inbox" layout            */
/* (used only when the Submitted tab is active)              */
/* ──────────────────────────────────────────────────────── */
function SubmissionCard({ r, onConfirm, onReject, onOpenReceipt }) {
  const location = useLocation();
  const paymentsBackFrom = `${location.pathname}${location.search || ""}`;
  const hasReceipt = !!r.receipt_path;
  // The file download endpoint accepts ?auth=<token> so inline <img> rendering works
  // even inside iframes where 3rd-party cookies are blocked.
  const token = getAccessToken();
  const receiptUrl = hasReceipt
    ? `${API_BASE}/files/${r.receipt_path}${token ? `?auth=${encodeURIComponent(token)}` : ""}`
    : null;
  const isImage = hasReceipt && /\.(png|jpe?g|gif|webp|heic|heif)$/i.test(r.receipt_path);

  return (
    <div
      data-testid={`submission-card-${r.id}`}
      className="overflow-hidden rounded-xl border border-amber-200 bg-white shadow-sm"
    >
      {/* Header — player + status */}
      <div className="border-b border-amber-100 bg-amber-50/60 px-5 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-heading text-lg font-bold text-ink-900 break-words" title={`${r.player?.first_name || ""} ${r.player?.last_name || ""}`.trim()}>
              {r.player?.first_name} {r.player?.last_name}
              {r.player?.jersey ? <span className="ml-1 text-ink-500">#{r.player.jersey}</span> : null}
            </h3>
            <p className="text-sm text-ink-600 break-words" title={`${r.team?.name || ""} · ${r.charge?.title || ""}`}>
              {r.team?.name}{" · "}
              {r.charge?.id ? (
                <Link
                  to={withReturnTo(`/coach/charges/${r.charge.id}`, { from: paymentsBackFrom, label: "Payments" })}
                  className="inline-flex items-center gap-0.5 font-semibold text-brand hover:underline focus:underline focus:outline-none"
                  data-testid={`submission-fee-link-${r.id}`}
                >
                  {r.charge?.title}
                  <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                </Link>
              ) : (
                r.charge?.title
              )}
            </p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1.5">
            <StatusBadge status={r.status} />
            <DueChip dueDate={r.charge?.due_date} paymentStatus={r.status} />
          </div>
        </div>
      </div>

      {/* Fee-amount discrepancy — surfaces when a coach edited the fee
          amount AFTER the parent submitted. Parent's submitted amount is
          preserved; coach sees the delta before deciding to approve. No
          silent rewrite of `r.amount`. */}
      {r.charge && Number(r.charge.amount) !== Number(r.amount) ? (
        <div
          className="border-b border-amber-100 bg-amber-100/70 px-5 py-2 text-sm text-amber-900"
          data-testid={`submission-amount-mismatch-${r.id}`}
        >
          <AlertTriangle className="mr-1.5 inline h-4 w-4 -mt-0.5" aria-hidden="true" />
          <span className="font-semibold">Amount doesn't match current fee.</span>{" "}
          Parent submitted <span className="font-semibold" data-testid={`submission-mismatch-submitted-${r.id}`}>{formatCurrency(r.amount)}</span>{" "}
          · current fee is now{" "}
          <span className="font-semibold" data-testid={`submission-mismatch-current-${r.id}`}>{formatCurrency(r.charge.amount)}</span>.
          {" "}Confirming will accept the parent's amount as recorded.
        </div>
      ) : null}

      {/* Body — details + receipt side by side on wide screens */}
      <div className="grid gap-4 p-5 md:grid-cols-5">
        {/* Details column */}
        <div className="space-y-3 md:col-span-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg bg-ink-100/50 p-3">
              <p className="label-caps">Amount</p>
              <p className="mt-1 font-heading text-2xl font-bold text-ink-900" data-testid={`submission-amount-${r.id}`}>
                {formatCurrency(r.amount)}
              </p>
            </div>
            <div className="rounded-lg bg-ink-100/50 p-3">
              <p className="label-caps">Paid via</p>
              <p
                className="mt-1 font-heading text-lg font-bold text-ink-900"
                data-testid={`submission-method-${r.id}`}
              >
                {formatMethod(r.method) || "—"}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="label-caps">Due date</p>
              <p className="mt-1 font-semibold text-ink-900">{formatDate(r.charge?.due_date)}</p>
            </div>
            <div>
              <p className="label-caps">Submitted</p>
              <p className="mt-1 font-semibold text-ink-900" data-testid={`submission-submitted-at-${r.id}`}>
                {formatDate(r.submitted_at)}
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-ink-200 bg-white p-3">
            <p className="label-caps">Parent note</p>
            <p className="mt-1 text-sm text-ink-900" data-testid={`submission-note-${r.id}`}>
              {r.note || <span className="italic text-ink-500">No note from parent.</span>}
            </p>
          </div>
        </div>

        {/* Receipt column */}
        <div className="md:col-span-2">
          <p className="label-caps">Proof of payment</p>
          {hasReceipt ? (
            <button
              type="button"
              onClick={() => onOpenReceipt({ url: receiptUrl, isImage, record: r })}
              data-testid={`submission-receipt-${r.id}`}
              className="mt-1 block w-full overflow-hidden rounded-lg border border-ink-200 bg-ink-100/40 hover:bg-ink-100 hover:border-brand/30 hover:ring-2 hover:ring-brand/10 transition-all text-left"
              title="Open full size"
            >
              {isImage ? (
                <img
                  src={receiptUrl}
                  alt="Payment receipt"
                  className="h-44 w-full object-contain bg-ink-100/60"
                />
              ) : (
                <div className="flex h-44 flex-col items-center justify-center gap-2 px-4 text-center text-sm text-ink-700">
                  <ImageIcon className="h-8 w-8 text-ink-400" />
                  <span className="font-semibold">View receipt</span>
                  <span className="text-xs text-ink-500">Tap to open</span>
                </div>
              )}
            </button>
          ) : (
            <div
              className="mt-1 flex h-44 flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-ink-200 bg-ink-100/40 px-4 text-center text-sm"
              data-testid={`submission-no-receipt-${r.id}`}
            >
              <ImageIcon className="h-8 w-8 text-ink-300" />
              <span className="font-semibold text-ink-700">No proof attached</span>
              <span className="text-xs text-ink-500">Parent didn&apos;t upload a screenshot.</span>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-2 border-t border-ink-100 bg-ink-100/40 px-5 py-3 sm:flex-row sm:justify-end">
        <Button
          variant="outline"
          onClick={() => onReject(r)}
          className="h-11 border-rose-300 font-semibold text-rose-700 hover:bg-rose-50"
          data-testid={`submission-reject-${r.id}`}
        >
          <XCircle className="mr-1 h-4 w-4" /> Reject
        </Button>
          <Button
          onClick={() => onConfirm(r.id)}
          className="h-11 bg-emerald-600 font-semibold text-white hover:bg-emerald-700"
          data-testid={`submission-confirm-${r.id}`}
        >
          <CheckCircle2 className="mr-1 h-4 w-4" /> Confirm payment
        </Button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────── */
/* Bulk-remind dialog — fires one reminder per   */
/* affected charge, friendly template by default */
/* ─────────────────────────────────────────── */
function BulkRemindDialog({ open, onOpenChange, unpaidRecords, onDone }) {
  const { terms } = useTerms();
  const [sending, setSending] = useState(false);

  // Group unpaid records by charge so we know how many distinct emails go out
  const byCharge = useMemo(() => {
    const map = new Map();
    for (const r of unpaidRecords) {
      const id = r.charge?.id;
      if (!id) continue;
      if (!map.has(id)) map.set(id, { charge: r.charge, records: [] });
      map.get(id).records.push(r);
    }
    return Array.from(map.values());
  }, [unpaidRecords]);

  const totalPeople = unpaidRecords.length;

  const send = async () => {
    setSending(true);
    let sentCount = 0;
    let failedCount = 0;
    let httpErrors = 0;
    // Fire one reminder per charge — backend tracks per-recipient delivery
    // and returns truthful `sent_count` / `failed_count` counters.
    await Promise.all(byCharge.map(async ({ charge, records }) => {
      try {
        const { data } = await api.post("/reminders", {
          charge_id: charge.id,
          scope: "selected",
          payment_record_ids: records.map((r) => r.id),
          subject: `Friendly reminder — ${charge.title}`,
          body:
            `Hi, quick reminder that ${charge.title} is still unpaid. ` +
            `Amount due: ${formatCurrency(charge.amount || 0)}. ` +
            `Please pay when you can and mark it paid in Squad Fee. Thanks!\n\n— ${terms.leader || "Coach"}`,
        });
        sentCount += Number(data?.sent_count ?? 0);
        failedCount += Number(data?.failed_count ?? 0);
      } catch {
        // The whole request failed — count every intended recipient as
        // failed so the summary doesn't undercount.
        httpErrors += records.length;
      }
    }));
    setSending(false);
    const totalFailed = failedCount + httpErrors;
    if (totalFailed === 0 && sentCount > 0) {
      toast.success(
        sentCount === 1 ? "Reminder sent." : `Reminders sent to ${sentCount} people.`
      );
    } else if (sentCount > 0 && totalFailed > 0) {
      toast.warning(`Sent to ${sentCount} of ${sentCount + totalFailed} recipients.`, {
        description:
          totalFailed === 1
            ? "1 email could not be delivered — try again in a few minutes."
            : `${totalFailed} emails could not be delivered — try again in a few minutes.`,
      });
    } else if (totalFailed > 0) {
      toast.error("Reminders could not be emailed.", {
        description: "Please try again in a few minutes.",
      });
    } else {
      toast.warning("No recipients to email.");
    }
    onOpenChange(false);
    onDone();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bulk-remind-dialog">
        <DialogHeader>
          <DialogTitle>Remind all unpaid</DialogTitle>
          <DialogDescription>
            Send a friendly reminder to every player who hasn&apos;t paid yet across the current view.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm">
            <p className="font-semibold text-amber-900">
              {totalPeople} {totalPeople === 1 ? "person" : "people"} across {byCharge.length} {byCharge.length === 1 ? "charge" : "charges"}
            </p>
            <p className="mt-1 text-xs text-amber-800">
              Uses the &quot;Friendly reminder&quot; template. Each charge gets one email per recipient.
            </p>
          </div>
          <ul className="max-h-48 overflow-y-auto rounded-lg border border-ink-200 divide-y divide-ink-100">
            {byCharge.map(({ charge, records }) => (
              <li key={charge.id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`bulk-remind-charge-${charge.id}`}>
                <span className="font-semibold text-ink-900 truncate">{charge.title}</span>
                <span className="shrink-0 ml-2 text-xs text-ink-500">{records.length} {records.length === 1 ? "person" : "people"}</span>
              </li>
            ))}
          </ul>
        </div>
        <DialogFooter>
          <Button
            onClick={send}
            disabled={sending || totalPeople === 0}
            className="h-11 bg-brand font-semibold text-white hover:bg-brand-hover"
            data-testid="bulk-remind-submit"
          >
            <Send className="mr-1 h-4 w-4" />
            {sending ? "Sending…" : `Send ${totalPeople} reminder${totalPeople === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────────────────────────── */
/* Receipt modal — opens proof of payment       */
/* full-size with metadata for context           */
/* ─────────────────────────────────────────── */
function ReceiptModal({ open, onOpenChange, payload }) {
  const [zoomed, setZoomed] = React.useState(false);
  React.useEffect(() => {
    // Reset zoom whenever the modal is closed so the next receipt opens
    // in the default (fit-to-viewport) state.
    if (!open) setZoomed(false);
  }, [open]);
  if (!payload) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent />
      </Dialog>
    );
  }
  const { url, isImage, record } = payload;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden" data-testid="receipt-modal">
        <DialogHeader className="border-b border-ink-100 bg-ink-100/40 px-5 py-3">
          <DialogTitle className="text-base">Proof of payment</DialogTitle>
          <DialogDescription className="text-xs">
            {record?.player?.first_name} {record?.player?.last_name} · {record?.charge?.title} · {formatCurrency(record?.amount)} via {formatMethod(record?.method) || "—"}
          </DialogDescription>
        </DialogHeader>
        {/* Pinch-zoom on mobile is native (touch-action:pinch-zoom); a
            tap toggles a 2x CSS zoom that pans on scroll — this covers
            both mouse (desktop tap-to-zoom) and touch (native pinch). */}
        <div
          className={`max-h-[70vh] bg-ink-100/60 flex items-center justify-center ${zoomed ? "overflow-auto" : "overflow-hidden"}`}
          style={{ touchAction: "pinch-zoom" }}
          data-testid="receipt-modal-viewport"
        >
          {isImage ? (
            <img
              src={url}
              alt="Payment receipt"
              onClick={() => setZoomed((z) => !z)}
              className={`transition-transform duration-200 select-none ${
                zoomed
                  ? "max-w-none max-h-none cursor-zoom-out scale-[2] origin-center"
                  : "max-h-[70vh] w-auto max-w-full object-contain cursor-zoom-in"
              }`}
              data-testid="receipt-modal-image"
              data-zoomed={zoomed ? "true" : "false"}
              draggable="false"
            />
          ) : (
            <div className="flex flex-col items-center gap-3 p-10 text-center">
              <ImageIcon className="h-12 w-12 text-ink-400" />
              <p className="font-semibold text-ink-900">This file can&apos;t be previewed here</p>
              <p className="max-w-sm text-sm text-ink-500">It might be a PDF or another non-image file.</p>
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-10 items-center gap-1 rounded-md bg-brand px-4 text-sm font-semibold text-white hover:bg-brand-hover"
                data-testid="receipt-modal-open-new-tab"
              >
                Open in new tab
              </a>
            </div>
          )}
        </div>
        <DialogFooter className="flex-row items-center justify-between gap-2 border-t border-ink-100 bg-ink-100/40 px-5 py-3">
          {isImage ? (
            <p className="text-xs text-ink-500" data-testid="receipt-modal-hint">
              Tap the image to zoom · pinch-to-zoom on mobile
            </p>
          ) : <span />}
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-10 shrink-0 items-center gap-1 rounded-md border border-ink-300 px-3 text-sm font-semibold text-ink-700 hover:bg-ink-100"
            data-testid="receipt-modal-fullsize-link"
          >
            Open full size in new tab
          </a>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────── */
/* Main page                */
/* ─────────────────────── */
export default function Payments() {
  const { terms } = useTerms();
  const [params, setParams] = useSearchParams();
  const urlStatus = params.get("status");
  // Default to "needs-approval" so the most actionable view is the landing state.
  const validInitial = URL_STATUS_TO_TAB[urlStatus] || "needs-approval";

  // Contextual Back — only shown when the coach opened Payments *from* a
  // specific team (e.g. via "Review Payments"). If Payments was reached
  // via the bottom nav directly, `?from=…` is absent and no Back appears.
  const back = useSmartBack(null, null);

  const [tab, setTab] = useState(validInitial);
  // If the URL carries ?team_id=…, pre-select that team filter (used by
  // "Review Payments" links from a specific Team page).
  const [teamId, setTeamId] = useState(params.get("team_id") || "all");
  const [q, setQ] = useState("");
  const [payments, setPayments] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  const [reminderRecord, setReminderRecord] = useState(null);
  const [recordRecord, setRecordRecord] = useState(null);
  const [rejectRecord, setRejectRecord] = useState(null);
  const [bulkRemindOpen, setBulkRemindOpen] = useState(false);
  const [receiptPayload, setReceiptPayload] = useState(null);

  useEffect(() => {
    api.get("/teams").then((r) => setTeams(r.data)).catch(() => setTeams([]));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      // Always fetch the team-filtered (but status-unfiltered) list so the
      // totals strip reflects the whole ledger. We filter by status client-side.
      const p = {};
      if (teamId !== "all") p.team_id = teamId;
      const r = await api.get("/payments", { params: p });
      setPayments(r.data);
    } catch {
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [teamId]);

  // Keep URL in sync with tab
  const switchTab = (id) => {
    setTab(id);
    const next = new URLSearchParams(params);
    // Default tab = "needs-approval" — only persist non-default in the URL
    if (id === "needs-approval") next.delete("status");
    else next.set("status", id);
    setParams(next, { replace: true });
  };

  const doAction = async (id, action, extra = {}) => {
    try {
      await api.post(`/payments/${id}/${action}`, extra);
      const verb = { confirm: "confirmed", reject: "rejected", waive: "waived" }[action] || action;
      // Auto-advance: on the To Review tab, after acting on a submission,
      // scroll the next visible submission card into view AND surface the
      // remaining count in the toast so the coach knows how many are left
      // without navigating away.
      let toastDescription;
      if (tab === "needs-approval") {
        const remaining = Math.max(filtered.filter((x) => x.id !== id).length, 0);
        if (remaining > 0) {
          toastDescription = remaining === 1 ? "1 more to review." : `${remaining} more to review.`;
          // Defer the scroll until after the list reloads so the next card
          // is actually mounted with its post-action position.
          setTimeout(() => {
            const remainingIds = filtered.filter((x) => x.id !== id).map((x) => x.id);
            for (const nextId of remainingIds) {
              const el = document.querySelector(`[data-testid="submission-card-${nextId}"]`);
              if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
                el.classList.add("ring-2", "ring-brand", "ring-offset-2");
                setTimeout(() => el.classList.remove("ring-2", "ring-brand", "ring-offset-2"), 1200);
                break;
              }
            }
          }, 350);
        } else {
          toastDescription = "You're all caught up.";
        }
      }
      toast.success(`Payment ${verb}`, toastDescription ? { description: toastDescription } : undefined);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const downloadCsv = () => {
    const csvParams = new URLSearchParams();
    if (teamId !== "all") csvParams.set("team_id", teamId);
    // Export only the currently-visible tab's statuses, so the CSV mirrors
    // what the coach sees on screen. Multi-status tabs (Unpaid / Paid) pass
    // a comma-list — backend keeps a single ?status= for back-compat.
    const tabDef = STATUS_TABS.find((t) => t.id === tab);
    if (tabDef && tabDef.statuses.length === 1) {
      csvParams.set("status", tabDef.statuses[0]);
    }
    window.open(`${API_BASE}/reports/csv?${csvParams.toString()}`, "_blank");
  };

  // Filter by status tab + free-text search
  const filtered = useMemo(() => {
    const tabDef = STATUS_TABS.find((t) => t.id === tab) || STATUS_TABS[0];
    let list = payments.filter((r) => tabDef.statuses.includes(r.status));
    if (q.trim()) {
      const needle = q.toLowerCase();
      list = list.filter((r) => {
        const name = `${r.player?.first_name || ""} ${r.player?.last_name || ""}`.toLowerCase();
        return (
          name.includes(needle) ||
          (r.charge?.title || "").toLowerCase().includes(needle) ||
          (r.team?.name || "").toLowerCase().includes(needle)
        );
      });
    }
    return list;
  }, [tab, q, payments]);

  // Per-tab counts so coaches can scan the workload at a glance
  const tabCounts = useMemo(() => {
    const counts = {};
    for (const td of STATUS_TABS) {
      counts[td.id] = payments.filter((r) => td.statuses.includes(r.status)).length;
    }
    return counts;
  }, [payments]);

  return (
    <AppLayout>
      {/* Contextual Back — only shown when the coach entered Payments *from*
          a specific team (via "Review Payments"). Direct bottom-nav entry
          shows nothing so the page still reads as the global Payments tab. */}
      {back.isContextual ? (
        <Link
          to={back.to}
          className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-ink-500 hover:text-ink-900"
          data-testid="payments-back-context"
        >
          <ChevronRight className="h-4 w-4 rotate-180" /> Back to {back.label}
        </Link>
      ) : null}
      {/* Header — simple, friendly, clipboard-style */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-ink-900 sm:text-4xl">Payments</h1>
          <p className="mt-1 text-base text-ink-600">Review, remind, and mark payments paid.</p>
        </div>
      </div>

      {/* Status tabs — 3 simple choices */}
      <div
        role="tablist"
        aria-label="Payment status"
        className="mt-6 -mx-2 overflow-x-auto px-2"
      >
        <div
          className="inline-flex w-max rounded-full border border-ink-200 bg-ink-100 p-1 text-sm font-semibold"
          data-testid="payments-status-tabs"
        >
          {STATUS_TABS.map((t) => {
            const count = tabCounts[t.id] || 0;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => switchTab(t.id)}
                className={`inline-flex h-10 items-center gap-1.5 whitespace-nowrap rounded-full px-5 transition-colors ${
                  active ? "bg-white text-ink-900 shadow-sm" : "text-ink-500 hover:text-ink-700"
                }`}
                data-testid={`payments-tab-${t.id}`}
              >
                {t.label}
                {count > 0 ? (
                  <span
                    className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-bold ${
                      active ? "bg-ink-900 text-white" : "bg-ink-200 text-ink-700"
                    }`}
                  >
                    {count}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      {/* Filters row */}
      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <select
          className="h-11 rounded-lg border border-ink-200 bg-white px-3 text-sm font-semibold text-ink-700"
          value={teamId}
          onChange={(e) => setTeamId(e.target.value)}
          data-testid="payments-team-filter"
        >
          <option value="all">All {terms.teams}</option>
          {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <div className="sm:ml-auto sm:w-72">
          <Input
            placeholder={`Search ${terms.athlete}, fee, or ${terms.team}…`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-11"
            data-testid="payments-search"
          />
        </div>
      </div>

      {/* List */}
      {tab === "unpaid" && !loading && filtered.length > 0 ? (
        <div
          className="mt-4 flex flex-col items-start gap-3 rounded-xl border border-amber-200 bg-amber-50/60 p-4 sm:flex-row sm:items-center sm:justify-between"
          data-testid="bulk-remind-banner"
        >
          <div>
            <h2 className="font-heading text-base font-bold text-ink-900">
              {filtered.length} {filtered.length === 1 ? `${terms.athlete} still owes` : `${terms.athletes} still owe`}
            </h2>
            <p className="text-sm text-ink-700">
              Send a friendly reminder to everyone in one tap.
            </p>
          </div>
          <Button
            onClick={() => setBulkRemindOpen(true)}
            className="h-11 bg-brand font-semibold text-white hover:bg-brand-hover"
            data-testid="bulk-remind-button"
          >
            <Send className="mr-1 h-4 w-4" /> Remind all unpaid
          </Button>
        </div>
      ) : null}

      {tab === "needs-approval" && !loading && filtered.length > 0 ? (
        <div className="mt-4 space-y-1" data-testid="submissions-inbox-header">
          <h2 className="font-heading text-lg font-bold text-ink-900">{filtered.length} {filtered.length === 1 ? "payment to review" : "payments to review"}</h2>
          <p className="text-sm text-ink-600">
            Check each payment and mark it paid if it looks correct.
          </p>
        </div>
      ) : null}

      {loading ? (
        <div className="mt-4 flex h-32 items-center justify-center rounded-xl border border-ink-100 bg-white">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand border-t-transparent" />
        </div>
      ) : filtered.length === 0 ? (
        <div
          className="mt-4 rounded-xl border border-ink-100 bg-white p-10 text-center shadow-sm"
          data-testid="payments-empty"
        >
          {tab === "needs-approval" ? (
            <>
              <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-500" />
              <p className="mt-3 font-semibold text-ink-900">You&apos;re all caught up</p>
              <p className="text-sm text-ink-500">No payments waiting for your approval.</p>
            </>
          ) : (
            <>
              <p className="font-semibold text-ink-900">Nothing here yet</p>
              <p className="text-sm text-ink-500">Try widening the filter or clear the search.</p>
            </>
          )}
        </div>
      ) : tab === "needs-approval" ? (
        // Rich "approval inbox" — one card per submission
        <div className="mt-4 grid gap-4 lg:grid-cols-2" data-testid="submissions-grid">
          {filtered.map((r) => (
            <SubmissionCard
              key={r.id}
              r={r}
              onConfirm={(id) => doAction(id, "confirm")}
              onReject={(rec) => setRejectRecord(rec)}
              onOpenReceipt={(payload) => setReceiptPayload(payload)}
            />
          ))}
        </div>
      ) : (
        // Standard row list for the other tabs
        <div className="mt-4 overflow-hidden rounded-xl border border-ink-100 bg-white shadow-sm">
          <div className="border-b border-ink-100 bg-ink-100/60 px-4 py-2 text-xs font-bold uppercase tracking-wide text-ink-500">
            Showing {filtered.length} {filtered.length === 1 ? "row" : "rows"}
          </div>
          <ul className="divide-y divide-ink-100">
            {filtered.map((r) => (
              <PaymentRow
                key={r.id}
                r={r}
                onAct={doAction}
                onRecord={(rec) => setRecordRecord(rec)}
                onRemind={(rec) => setReminderRecord(rec)}
                onReject={(rec) => setRejectRecord(rec)}
              />
            ))}
          </ul>
        </div>
      )}

      {/* Dialogs */}
      <RecordPaymentDialog
        open={!!recordRecord}
        onOpenChange={(o) => { if (!o) setRecordRecord(null); }}
        record={recordRecord}
        onDone={load}
      />
      <RejectDialog
        open={!!rejectRecord}
        onOpenChange={(o) => { if (!o) setRejectRecord(null); }}
        record={rejectRecord}
        onDone={load}
      />
      <SendReminderDialog
        open={!!reminderRecord}
        onOpenChange={(o) => { if (!o) setReminderRecord(null); }}
        charge={reminderRecord?.charge}
        initialPaymentIds={reminderRecord ? [reminderRecord.id] : []}
      />
      <BulkRemindDialog
        open={bulkRemindOpen}
        onOpenChange={setBulkRemindOpen}
        unpaidRecords={filtered.filter((r) => ["unpaid", "rejected"].includes(r.status))}
        onDone={load}
      />
      <ReceiptModal
        open={!!receiptPayload}
        onOpenChange={(o) => { if (!o) setReceiptPayload(null); }}
        payload={receiptPayload}
      />

      {/* Subtle footer link — Export CSV moved out of the prime header to keep
          the page feeling like a coach clipboard, not an accounting tool. */}
      {payments.length > 0 ? (
        <div className="mt-10 flex justify-end">
          <button
            type="button"
            onClick={downloadCsv}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink-500 hover:text-ink-900"
            data-testid="payments-export-csv"
          >
            <Download className="h-4 w-4" /> Export to CSV
          </button>
        </div>
      ) : null}
    </AppLayout>
  );
}
